import type { CampaignOffer, PlanPrice } from "./billingApi";
import { parseApiInstant, zonedWallClock } from "./localeFormat";

export const TIER_LABELS: Record<string, string> = {
  starter: "Basic",
  professional: "Pro",
  enterprise: "Enterprise",
};

export const PLAN_NAV_LABELS: Record<string, string> = {
  starter: "Basic",
  professional: "Professional",
  enterprise: "Enterprise",
};

export function planLabel(tier: string): string {
  return TIER_LABELS[tier] ?? tier;
}

export function planNavLabel(tier: string | null | undefined): string | null {
  if (!tier) return null;
  return PLAN_NAV_LABELS[tier] ?? null;
}

export function seatBandLabel(plan: Pick<PlanPrice, "min_seats" | "max_seats" | "seats">): string {
  const min = plan.min_seats ?? plan.seats;
  if (plan.max_seats == null) return `${min}+ users`;
  return `${min}–${plan.max_seats} users`;
}

export function seatsFitPlan(plan: PlanPrice, seats: number): boolean {
  const min = plan.min_seats ?? 1;
  if (seats < min) return false;
  if (plan.max_seats != null && seats > plan.max_seats) return false;
  return true;
}

export function billedSeatsForActiveUsers(plan: PlanPrice, activeUsers: number): number | null {
  const min = plan.min_seats ?? plan.seats ?? 1;
  const active = Math.max(0, Math.floor(activeUsers));
  if (plan.max_seats != null && active > plan.max_seats) return null;
  return Math.max(min, active);
}

/** A bare YYYY-MM-DD is a calendar day; a timestamp is read in `timezone` (the browser's when omitted). */
export function formatAckDate(value: Date | string, timezone?: string): string {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return `${String(day).padStart(2, "0")}${months[month - 1]}${year}`;
  }
  const date = parseApiInstant(value);
  if (Number.isNaN(date.getTime())) return "";
  const wall = zonedWallClock(date, timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
  return `${String(wall.day).padStart(2, "0")}${months[wall.month - 1]}${wall.year}`;
}

export function monthlyCents(plan: PlanPrice, seats: number): number {
  const unit = plan.local_amount_cents ?? plan.amount_cents;
  if (plan.billing_unit === "seat" || plan.app_code === "crm") return unit * seats;
  return unit;
}

export function quoteCampaign(monthly: number, campaign: CampaignOffer | null | undefined): {
  monthlyCents: number;
  yearlyListCents: number;
  payNowCents: number;
  saveCents: number;
} {
  const yearlyListCents = monthly * 12;
  if (!campaign) {
    return { monthlyCents: monthly, yearlyListCents, payNowCents: yearlyListCents, saveCents: 0 };
  }
  let payNowCents = yearlyListCents;
  if (campaign.discount_kind === "free_months") {
    payNowCents = monthly * Math.max(0, 12 - campaign.discount_value);
  } else if (campaign.discount_kind === "percent_off") {
    const base = campaign.interval === "year" ? yearlyListCents : monthly;
    payNowCents = Math.round(base * (100 - Math.min(campaign.discount_value, 100)) / 100);
  }
  return {
    monthlyCents: monthly,
    yearlyListCents,
    payNowCents,
    saveCents: Math.max(0, yearlyListCents - payNowCents),
  };
}

export function applyPercentOff(amountCents: number, percent: number): number {
  const pct = Math.min(Math.max(percent, 0), 100);
  return Math.round(Math.max(0, amountCents) * (100 - pct) / 100);
}

export function moneyLabel(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(cents / 100);
}
